'use client';
import React, { useState, useCallback, useEffect } from 'react';
import { apiURL } from '../../../../common/urls';
import interactionResponse from 'await-interaction-response';
import * as stylex from '@stylexjs/stylex';
import { colors } from '@/common/tokens.stylex';

type Props = {
  bodyItem: {
    readonly answers: readonly {
      readonly id: string;
      readonly text: string;
      readonly voteCount: number;
    }[];
    readonly id: any;
    readonly isClosed: boolean;
    readonly title: string;
    readonly voteCount: number;
  };
};

function BodyItemPoll(props: Props) {
  const { bodyItem } = props;
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [skipVote, setSkipVote] = useState(false);
  const [votesCount] = useState(
    bodyItem.answers.reduce((prev, answer) => prev + answer.voteCount, 0),
  );
  const [voted, setVoted] = useState(false);
  const vote = useCallback(async () => {
    if (!selectedAnswer) return;
    const answer = bodyItem.answers.find((a) => a.id === selectedAnswer);
    if (!answer) return console.error('Ungültige Antwort');

    interactionResponse().then(async () => {
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'poll_vote',
        poll_vote: {
          poll_question: bodyItem.title,
          poll_answer: answer.text,
          poll_id: bodyItem.id,
        },
      });

      const response = await fetch(apiURL + '/polls/' + bodyItem.id, {
        method: 'POST',
        body: JSON.stringify({
          answer_id: answer.id,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.status !== 200) {
        console.error('Failed to vote');
      }
    });

    setVoted(true);
    setSelectedAnswer(answer.id);
  }, [bodyItem, selectedAnswer]);
  const onAnswerChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedAnswer(e.currentTarget.value);
    },
    [],
  );

  useEffect(() => {
    if (selectedAnswer) {
      vote();
    }
  }, [selectedAnswer, vote]);

  const renderAnswer = (answer: Props['bodyItem']['answers'][number]) => {
    let { voteCount: answerVotesCount } = answer;
    const showResult = voted || skipVote || bodyItem.isClosed;
    const isSelectedAnswer = answer.id === selectedAnswer;

    if (showResult) {
      if (isSelectedAnswer) answerVotesCount += 1;
    }
    return (
      <div
        {...stylex.props(
          styles.answer,
          stylex.defaultMarker(),
          isSelectedAnswer ? styles.answerSelected : styles.answerIdle,
          showResult ? styles.answerDone : styles.answerActive,
        )}
        key={'result' + answer.id}
        onClick={() => {
          if (!showResult) {
            setSelectedAnswer(answer.id);
            vote();
          }
        }}
      >
        <div {...stylex.props(styles.choiceCol)}>
          {showResult ? (
            <div {...stylex.props(styles.voteCount)}>{answerVotesCount}</div>
          ) : (
            <input
              id={answer.id}
              type="radio"
              name="poll_answer"
              {...stylex.props(styles.radio)}
            />
          )}
        </div>
        <div {...stylex.props(styles.answerBody)}>
          <label {...stylex.props(styles.label)} htmlFor={answer.id}>
            {answer.text}
          </label>
          <div
            {...stylex.props(
              styles.barTrack,
              showResult ? styles.barTrackVisible : styles.barTrackHidden,
            )}
          >
            <div
              {...stylex.props(styles.barFill)}
              style={{
                flex: showResult ? answerVotesCount : 0,
              }}
            />
            <div
              style={{
                flex: showResult
                  ? Math.max(votesCount - answerVotesCount, 0)
                  : 1,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (!bodyItem || !bodyItem.title || !Array.isArray(bodyItem.answers)) {
    console.log('Invalid poll received');
    return null;
  }

  const answers = bodyItem.answers.map((answer) => renderAnswer(answer));
  const userCanVote = !bodyItem.isClosed && !voted && !skipVote;

  const root = stylex.props(styles.root);

  return (
    <div
      {...root}
      style={{
        ...root.style,
        order: 100,
      }}
    >
      <div {...stylex.props(styles.headingWrap)}>
        <span {...stylex.props(styles.heading)}>{bodyItem.title}</span>
        {/* {!userCanVote && (
          <span>
            {votesCount} Stimmen
          </span>
        )} */}
      </div>
      <div {...stylex.props(styles.answers)}>{answers}</div>
      <div {...stylex.props(styles.footer)}>
        {/* {userCanVote && (
          <button
            disabled={!selectedAnswer}
            onClick={vote}
          >
            Abstimmen
          </button>
        )} */}
        {userCanVote && (
          <button
            {...stylex.props(styles.skipButton)}
            onClick={() => setSkipVote(true)}
          >
            Ergebnis anzeigen
          </button>
        )}
      </div>
    </div>
  );
}

export default BodyItemPoll;

const styles = stylex.create({
  root: {
    marginBlock: 24,
    marginInline: 0,
    width: '100%',
    maxWidth: 750,
  },
  headingWrap: {
    marginBottom: 20,
  },
  heading: {
    display: 'block',
    fontWeight: 600,
    fontSize: 19.5,
  },
  answers: {
    marginTop: 20,
    marginInline: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  answer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginInline: 0,
    padding: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 12,
  },
  answerSelected: {
    borderColor: colors.primaryColor,
    backgroundColor: {
      default: colors.lightPink,
      ['@media (prefers-color-scheme: dark)']: colors.adsBackground,
    },
  },
  answerIdle: {
    borderColor: colors.lightGrey,
  },
  answerDone: {
    cursor: 'default',
  },
  answerActive: {
    cursor: 'pointer',
    borderColor: {
      ':hover': colors.primaryColor,
    },
  },
  choiceCol: {
    display: 'flex',
    width: 40,
    flexShrink: 0,
    justifyContent: 'center',
  },
  voteCount: {
    color: colors.tintColor,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  radio: {
    appearance: 'none',
    margin: 0,
    boxSizing: 'content-box',
    width: 20,
    height: 20,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: {
      default: colors.grey,
      ':checked': colors.tintColor,
      [stylex.when.ancestor(':hover')]: colors.tintColor,
    },
    borderRadius: '50%',
    outline: 'none',
    display: 'block',
    cursor: 'pointer',
    backgroundColor: {
      default: 'transparent',
      ':checked': colors.lightGrey,
    },
    flexShrink: 0,
    '::before': {
      content: '""',
      display: 'block',
      width: '70%',
      height: '70%',
      marginBlock: '15%',
      marginInline: 'auto',
      borderRadius: '50%',
      backgroundColor: {
        default: 'transparent',
        ':checked': colors.tintColor,
      },
    },
  },
  answerBody: {
    minWidth: 0,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '0%',
  },
  barTrack: {
    display: 'flex',
    height: 4,
    marginTop: 8,
    overflow: 'hidden',
    borderRadius: 12,
  },
  barTrackVisible: {
    backgroundColor: colors.lightGrey,
  },
  barTrackHidden: {
    backgroundColor: 'transparent',
  },
  barFill: {
    height: 4,
    borderRadius: 12,
    flexGrow: 1,
    backgroundColor: colors.tintColor,
    transitionProperty: 'all',
    transitionDuration: '300ms',
    transitionDelay: '150ms',
  },
  label: {
    display: 'flex',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    marginTop: 24,
    minHeight: 24,
    justifyContent: 'space-between',
  },
  skipButton: {
    cursor: 'pointer',
    borderStyle: 'none',
    backgroundColor: 'transparent',
    fontSize: 16,
    fontWeight: 600,
    color: {
      default: colors.darkgrey,
      ':hover': colors.tintColor,
      ['@media (prefers-color-scheme: dark)']: {
        default: colors.lightGrey,
        ':hover': colors.tintColor,
      },
    },
    borderRadius: 12,
  },
});
